import { environment } from '@core/environment';
import { useCachedAuth0Url } from '@core/logic';
import type { TexturedMesh } from '@processingResults/types';
import { useEffect, useState } from 'react';
import type { BufferGeometry } from 'three';
import { DRACOLoader } from 'three-stdlib';

const dracoLoader = new DRACOLoader();
/**
 * Using CDN of google as a runtime external dependency for draco setup here.
 * If this may break in the future, me may replicate
 * the "/javascript" dir of the drao repo @ https://github.com/google/draco
 * into out frontend repo at /public/assets/draco/ and set the decoder path to it
 */
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.4.1/');

export const useDracoGeometry = (texturedMesh?: TexturedMesh): BufferGeometry | undefined => {
  const [url, setUrl] = useState<string>();
  const cachedUrl = useCachedAuth0Url(url);
  const [geometry, setGeometry] = useState<BufferGeometry>();

  // Cleanly dispose of the old geometry when it changes or on unmount
  useEffect(() => {
    return () => {
      geometry?.dispose();
    };
  }, [geometry]);

  useEffect(() => {
    const gsPath = texturedMesh?.mesh?.location;
    if (!texturedMesh || !gsPath || !gsPath.startsWith('gs://')) {
      if (url !== undefined) {
        setUrl(undefined);
        setGeometry(undefined);
      }
      return;
    }

    const nextUrl = `${environment.apiUrl}/texturedMeshes/${texturedMesh.id}/downloads/mesh`;
    if (url !== nextUrl) {
      setGeometry(undefined);
      setUrl(nextUrl);
    }
  }, [texturedMesh, url]);

  useEffect(() => {
    if (!cachedUrl) return undefined;
    let cancelled = false;
    dracoLoader
      .loadAsync(cachedUrl)
      .then((next) => {
        if (cancelled) {
          next.dispose();
          return;
        }
        setGeometry(next);
      })
      .catch((err) => {
        if (!cancelled) console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, [cachedUrl]);

  return geometry;
};
